"use client";

import React from "react";
import { Col, Form, InputGroup, Row } from "react-bootstrap";
import { useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Button, ButtonStyleType } from "@into-discovery/browsewebshared";
import { Link } from "/navigation";
import color from "../../styles/_helpers/branding.module.scss";
import { IContactProps } from "/models/contact/IContactProps";
import { academicYearList, currentYear, getMonthList, getSubjectList } from "/utils/contact-us-items";
import { ContactSubmission } from "/models/contact/IContactSubmission";
import { postEnquiry } from "/api-clients/enrollment-api-client";
import { appInsights } from "/utils/appinsights-helper";
import ReadUTMQueryParameters from "../../hooks/use-localstorage.hook";

type Props = {
  pageProps: IContactProps;
};

export default function ContactProvider({ pageProps }: Props) {
  const currentLocale = useLocale();
  const translate = useTranslations(`Contact`);
  const monthList = getMonthList(useTranslations);
  const subjectList = getSubjectList(useTranslations);
  const yearRef = useRef<HTMLInputElement | null>(null);
  const monthRef = useRef<HTMLSelectElement | null>(null);
  const [validated, setValidated] = useState<boolean>(false);
  const [formApplied, setFormApplied] = useState<boolean>(false);
  const [maxDateValidation, setMaxDateValidation] = useState<number>(31);
  const [currentlyLocatedCountry, setCurrentlyLocatedCountry] = useState<string | null>(null);
  const countryCode = pageProps.countryCode;
  const legalUrl = `${process.env.NEXT_PUBLIC_INTO_STUDY_URL}/${currentLocale}/legal-and-privacy-policy/into`;

  const setMaxDayValidation = () => {
    const year = parseInt(yearRef.current?.value || "0", 10);
    const month = parseInt(monthRef.current?.value || "0", 10);
    const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) {
      monthLength[1] = 29;
    }

    setMaxDateValidation(monthLength[month]);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const utmParams = ReadUTMQueryParameters();
    const form = event.currentTarget as HTMLFormElement;

    event.preventDefault();
    event.stopPropagation();

    if (form.checkValidity()) {
      const getFormFieldValue = (name: string): string => {
        const element = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | RadioNodeList | null;

        if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement) {
          return element.value || "";
        }

        if (element instanceof RadioNodeList && element.value) {
          return element.value || "";
        }

        return "";
      };

      const getFormFieldBoolean = (name: string): boolean => {
        const element = form.elements.namedItem(name) as HTMLInputElement | null;

        return !!(element && element.type === "checkbox" && element.checked);
      };

      const formData: ContactSubmission = {
        FirstName: getFormFieldValue("firstName"),
        LastName: getFormFieldValue("lastName"),
        DateOfBirth: new Date(
          Date.UTC(
            parseInt(getFormFieldValue("birthYear") || "0"),
            parseInt(getFormFieldValue("birthMonth") || "0"),
            parseInt(getFormFieldValue("birthDay") || "0")
          )
        ),
        Email: getFormFieldValue("emailAddress"),
        MobilePhone: `${getFormFieldValue("dialCode")}${getFormFieldValue("phoneNumber")}`,
        ContactNotes: getFormFieldValue("contactNotes"),
        CountryOfResidence: getFormFieldValue("countryOfResidence"),
        CurrentlyLocatedCountry: getFormFieldValue("currentlyLocatedCountry"),
        ChinaPIPLConsent: getFormFieldValue("currentlyLocatedCountry") === "a063000000CZoZH" ? "Yes" : null,
        AcademicYear: getFormFieldValue("academicYear"),
        CountryOfStudy: countryCode,
        SubjectToStudy: getFormFieldValue("subjectToStudy") === "default" ? "" : getFormFieldValue("subjectToStudy"),
        GdprContactPermission: getFormFieldBoolean("gdprContactPermission"),
        RelatedCampaign: `7013A000000ZAZwQAO`,
        OnlineEnquiry: true,
        SubmissionDate: new Date(),
        CenterOfInterest: [],
        Source: utmParams ? utmParams?.replaceAll("&", ",")?.replaceAll("=", ":") : null
      };

      try {
        const enquiryResponse = await postEnquiry(formData);
        if (enquiryResponse.status >= 400) {
          appInsights.trackBackendError(new Error(`Error posting the form - ${enquiryResponse?.statusText}`));
        } else {
          setFormApplied(true);
        }
      } catch (error) {
        appInsights.trackBackendError(error as Error, "handleSubmit");
      }
    }

    setValidated(true);
  };

  return (
    <>
      {!formApplied ? (
        <>
          <h2 className="mb-md-3 mb-2">{translate(`contactUsBtn`)}</h2>
          <p className="mb-md-4 mb-2">{translate(`intro`)}</p>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Row>
              <Col xs="12" md="6">
                <Form.Group controlId="firstName" className="mb-3">
                  <Form.Label>{translate(`firstName`)}</Form.Label>
                  <Form.Control required type="text" />
                  <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group controlId="lastName" className="mb-3">
                  <Form.Label>{translate(`lastName`)}</Form.Label>
                  <Form.Control required type="text" />
                  <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="birthDay">{translate(`dateOfBirth`)}</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Control
                      id="birthDay"
                      required
                      placeholder={translate(`dayPlaceholder`)}
                      type="number"
                      min="1"
                      max={maxDateValidation}
                      aria-label="enter date of your birthday"
                    />
                    <Form.Select
                      ref={monthRef}
                      onChange={setMaxDayValidation}
                      id="birthMonth"
                      defaultValue=""
                      aria-label="select month of your birthday"
                      required
                    >
                      <option value="" disabled hidden>
                        {translate(`monthPlaceholder`)}
                      </option>
                      {monthList.map((option) => (
                        <option key={option.text} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      ref={yearRef}
                      onChange={setMaxDayValidation}
                      id="birthYear"
                      required
                      placeholder={translate(`yearPlaceholder`)}
                      type="number"
                      min={currentYear - 80}
                      max={currentYear - 10}
                      aria-label="enter year of your birthday"
                    />
                    <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-3" controlId="emailAddress">
                  <Form.Label>{translate(`emailAddress`)}</Form.Label>
                  <Form.Control required pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$" type="email" />
                  <Form.Control.Feedback type="invalid">{translate(`invalidEmail`)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="phoneNumber">{translate(`mobileNumber`)}</Form.Label>
                  <InputGroup hasValidation>
                    <Form.Select
                      id="dialCode"
                      defaultValue=""
                      style={{ maxWidth: "40%" }}
                      required
                      aria-label="select your country calling code"
                    >
                      <option value="" disabled hidden></option>
                      {pageProps?.countryCallingCodes.map((option) => (
                        <option key={option.text} value={option.value}>
                          {option.text}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control
                      id="phoneNumber"
                      required
                      type="number"
                      min="0"
                      aria-label="enter your mobile number here without country calling code"
                    />
                    <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                <Form.Group className="mb-3" controlId="countryOfResidence">
                  <Form.Label>{translate(`whereDoYouLive`)}</Form.Label>
                  <Form.Select defaultValue="" required>
                    <option value="" disabled hidden></option>
                    {pageProps?.countryList.map((option) => (
                      <option key={option.text} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="currentlyLocatedCountry">
                  <Form.Label>{translate(`whatCountryCurrentlyLocatedIn`)}</Form.Label>
                  <Form.Select
                    defaultValue=""
                    required
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onChange={(e: any) => setCurrentlyLocatedCountry(e.target.value)}
                  >
                    <option value="" disabled hidden></option>
                    {pageProps?.countryList.map((option) => (
                      <option key={option.text} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                </Form.Group>
                {currentlyLocatedCountry === "a063000000CZoZH" && (
                  <>
                    <Form.Group className="mb-3" controlId="piplCompliance">
                      <Form.Label>{translate(`piplCompliance.helpText`)}</Form.Label>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy1">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`piplCompliance.copy1`)} <Link href={legalUrl}>INTO`s Privacy Notice</Link>.
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">{translate(`piplCompliance.error`)}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy2">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`piplCompliance.copy2a`)} <Link href={legalUrl}>INTO`s Privacy Notice</Link>
                        {translate(`piplCompliance.copy2b`)}
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">{translate(`piplCompliance.error`)}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy3">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`piplCompliance.copy3a`)}
                        <Link href={legalUrl}>INTO`s Privacy Notice</Link>
                        {translate(`piplCompliance.copy3b`)}
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">{translate(`piplCompliance.error`)}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="piplComplianceCopy4">
                      <Form.Check.Input required type="checkbox" />
                      <Form.Check.Label className="mx-2 calc-width-2rem">
                        {translate(`piplCompliance.copy4`)}
                        <Link href={legalUrl}>INTO`s Privacy Notice</Link>.
                      </Form.Check.Label>
                      <Form.Control.Feedback type="invalid">{translate(`piplCompliance.error`)}</Form.Control.Feedback>
                    </Form.Group>
                  </>
                )}
                <Form.Group className="mb-3" controlId="academicYear">
                  <Form.Label>{translate(`whenStartStudying`)}</Form.Label>
                  <Form.Select defaultValue="" required>
                    <option value="" disabled hidden></option>
                    {academicYearList.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{translate(`validation.required`)}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3" controlId="subjectToStudy">
                  <Form.Label>{translate(`whichSubjectStudy`)}</Form.Label>
                  <Form.Select defaultValue="default">
                    <option value="default" disabled hidden></option>
                    {subjectList.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.text}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3" controlId="gdprContactPermission">
                  <p>
                    <strong>
                      {countryCode === "USA"
                        ? translate("hearMoreFromINTOBeforeUS")
                        : translate("hearMoreFromINTOBeforeUKAU")}
                    </strong>
                  </p>
                  <Form.Check.Input type="checkbox" />
                  <Form.Check.Label className="mx-2">{translate(`hearMoreFromINTO`)}</Form.Check.Label>
                  <Form.Control.Feedback type="invalid">{translate(`validation.gdpr`)}</Form.Control.Feedback>
                  <p></p>
                  <p>
                    <strong>
                      {translate("hearMoreFromINTOAfter")}
                      <a href={legalUrl}>{translate("hearMoreFromINTOAfterHere")}</a>
                      {translate("hearMoreFromINTOAfterDot")}
                    </strong>
                  </p>
                </Form.Group>
                <Form.Group className="mb-3" controlId="contactNotes">
                  <Form.Label>{translate(`howCanWeHelp`)}</Form.Label>
                  <Form.Control
                    className="h-auto"
                    rows={5}
                    maxLength={250}
                    as="textarea"
                    placeholder={translate(`helpPlaceholder`)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              activeBackgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
              backgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
              copyColor={color.white}
              hoverBackgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
              buttonAriaLabel="Submit contact us form"
              buttonID="contact-us"
              buttonName="contact-us"
              buttonRole="button"
              buttonStyle={ButtonStyleType.Filled}
              buttonType="submit"
            >
              {translate(`contactUsBtn`)}
            </Button>
          </Form>
        </>
      ) : (
        <div>
          <h3 className="mb-md-3 mb-2">{translate(`thankYouMessage`)}</h3>
          <hr />
          <Button
            activeBackgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
            backgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
            copyColor={color.white}
            hoverBackgroundColor={pageProps?.invertedTheme ? pageProps?.brandPrimaryColor : pageProps?.themeColor}
            buttonAriaLabel="Back to home"
            buttonID="back-to-home"
            buttonName="mack-to-home"
            buttonRole="link"
            buttonStyle={ButtonStyleType.Filled}
            buttonURL={`/${currentLocale}`}
          >
            {translate(`backToHomeBtn`)}
          </Button>
        </div>
      )}
    </>
  );
}
